import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import ComponentLoading from "../../../../components/Common/ComponentLoading";
import { fetchBillingDetail, reduceInvoiceBilling } from "../../../../store/slices/billing";
import { Box, Grid, Typography } from "@material-ui/core";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import BillingDetailTable from "./BillingDetailTable";
import { makeStyles } from "@material-ui/core/styles";
import SummaryTable from "./SummaryTable";
import AppUtil from "../../../../utilities/AppUtil";
import { Label } from "reactstrap";
import { reduceExportBookingBilling } from "../../../../store/slices/booking";
import { fetchWorkerList } from "../../../../store/slices/worker";
import { fetchAllJobs } from "../../../../store/slices/job";
import AppSelect from 'components/Select/AppSelect';

const useStyle = makeStyles(() => ({
    title: {
        fontSize: '22px',
        fontWeight: '500',
        color: AppColor.black
    },
    box: {
        backgroundColor: 'rgba(196, 196, 196, 0.3)',
        border: '1px solid rgba(0, 0, 0, 0.2)',
        borderRadius: '5px',
        marginLeft: '8px',
        minWidth: '120px',
        padding: '5px',
        textAlign: 'right',
        fontSize: '16px'
    },
    text: {
        fontSize: '16px'
    },
    input: {
        '&>div': {
            border: '1px solid rgba(0, 0, 0, 0.2)',
            borderRadius: '3px',
            height: '36px',
            padding: '0px 8px'
        }
    },
    select: {
        padding: '0px 0.5rem',
        width: "100%"
    },
    label: {
        fontSize: '14px',
        color: AppColor.black,
        fontWeight: '700',
        marginBottom: 0,
        marginRight: '0.5rem'
    },
    container: {
        border: '1px solid rgba(0, 0, 0, 0.27)',
        borderRadius: '10px'
    }
}))

const BillingDetail = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const classes = useStyle();

    const { id } = useParams();

    const isAdmin = location.pathname.includes('admin');

    const [data, setData] = useState({});
    const [workers, setWorkers] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedJob, setSelectedJob] = useState();
    const [selectedWorker, setSelectedWorker] = useState();

    const _export = async () => {
        setExporting(true);
        await dispatch(reduceExportBookingBilling({
            billingId: id,
            workerId: selectedWorker || "",
            jobId: selectedJob || ""
        }));
        setExporting(false);
    }

    const _invoice = async () => {
        setSubmitting(true);
        const { payload } = await dispatch(reduceInvoiceBilling(id));
        setSubmitting(false);
        if (payload) {
            _loadData();
        }
    }

    const _workerChange = (event) => {
        setSelectedWorker(event.target.value);
        _loadJobs(event.target.value);
    }

    const _jobChange = (event) => {
        setSelectedJob(event.target.value);
        _loadWorkers(event.target.value);
    }

    const _loadData = async () => {
        const { payload } = await dispatch(fetchBillingDetail(id));
        if (payload) {
            setData(payload);
        }
    }

    const _loadJobs = async (workerId) => {
        const { payload } = await dispatch(fetchAllJobs({
            billingId: id,
            workerId: workerId || ""
        }));
        if (payload) {
            setJobs(payload);
        }
    }

    const _loadWorkers = async (jobId) => {
        const { payload } = await dispatch(fetchWorkerList({
            billingId: id,
            jobId: jobId || ""
        }));
        if (payload) {
            setWorkers(payload);
        }
    }

    useEffect(() => {
        _loadData().then(() => setLoading(false));
        if (!isAdmin) {
            _loadJobs();
            _loadWorkers();
        }
    }, []);

            // eslint-disable-next-line react/prop-types
            const renderSelectField = ({ name, placeholder, options, onChange }) => (
                <AppSelect
                    name={name}
                    placeholder={t(placeholder)}
                    options={options.map((item) => {
                        return {
                            value: item.id,
                            label: name === 'workers' ? `${item?.firstName} ${item?.lastName}` : item?.title
                        }
                    })}
                    onChange={option => {
                        const params = {};
                        if (option) {
                            params[name] = option.value;
                            onChange && onChange({
                                target: {
                                    name: name,
                                    value: option.value
                                }
                            })
                        } else {
                            params[name] = '';
                            onChange && onChange({
                                target: {
                                    name: name,
                                    value: ''
                                }
                            })
                        }
                    }}
                    allowOverflow={true}
                    className={classes.select}
                />
            );

    if (loading) {
        return <ComponentLoading />;
    }
    return (
        <Box>
            {
                (data.billingStatus === 'PAID' || data.billingStatus === 'PAID_QB') && (
                    <Box>
                        {t('label.billing_id')}: <strong>{data.billingStatus === 'PAID_QB' ? data.qboId : data.xeroBillingId}</strong>
                    </Box>
                )
            }
            {!isAdmin && (
                <Grid container spacing={3} className={'my-3'}>
                    <Grid item sm={12} md={6}>
                        <Box display={'flex'} alignItems={'center'}>
                            <Label className={classes.label}>{t('label.worker')}</Label>
                            {
                                renderSelectField({
                                    name: 'workers',
                                    placeholder: "label.all",
                                    options: workers,
                                    onChange: (e) => _workerChange(e)
                                })
                            }
                            {/* <AppSelect
                                placeholder={t('label.all')}
                                value={selectedWorker}
                                option={workers.map((item) => {
                                    return {
                                        value: item.id,
                                        label: `${item?.firstName} ${item?.lastName}`
                                    }
                                })}
                                className={classes.select}
                                onChange={_workerChange}
                            /> */}
                        </Box>
                    </Grid>
                    <Grid item sm={12} md={6}>
                        <Box display={'flex'} alignItems={'center'}>
                            <Label className={classes.label}>{t('label.job')}</Label>
                            {
                                renderSelectField({
                                    name: 'jobs',
                                    placeholder: "label.all",
                                    options: jobs,
                                    onChange: (e) => _jobChange(e)
                                })
                            }
                            {/* <AppSelect
                                placeholder={t('label.all')}
                                value={selectedJob}
                                option={jobs.map((item) => {
                                    return {
                                        value: item.id,
                                        label: item?.title
                                    }
                                })}
                                className={classes.select}
                                onChange={_jobChange}
                            /> */}
                        </Box>
                    </Grid>
                </Grid>
            )}
            <Box mt={2} display="flex" justifyContent={isAdmin ? 'flex-end' : 'center'}>
                <AppButton
                    label={t('label.export_csv_file')}
                    isLoading={exporting}
                    disabled={exporting}
                    onClick={() => _export()}
                />
            </Box>
            <Box
                className={!isAdmin ? classes.container : undefined}
                p={!isAdmin ? 3 : undefined}
                mt={3}
            >
                <BillingDetailTable
                    detail={data}
                    worker={selectedWorker}
                    job={selectedJob}
                />
                <Box mt={5}>
                    <Typography className={classes.title}>
                        {t('title.summary')}
                    </Typography>
                </Box>
                <SummaryTable data={data.summaries} />
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Box>
                        <Box display="flex" justifyContent="flex-end" alignItems="center">
                            <Typography className={classes.text}>
                                {t('label.total_hours')}:
                            </Typography>
                            <Box className={classes.box}>
                                <strong>{AppUtil.formatMoney(data.totalHours)}</strong>
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                            <Typography className={classes.text}>
                                {t('label.total_price')}:
                            </Typography>
                            <Box className={classes.box}>
                                <strong>${AppUtil.formatMoney(data.totalPrice)}</strong>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box mt={5} display="flex" justifyContent="flex-end" alignItems="center">
                    <AppButton
                        label={t('button.back')}
                        onClick={() => history.goBack()}
                        mode="outlined"
                        className={'mr-2'}
                    />
                    {isAdmin && (
                        <AppButton
                            label={t('button.submit')}
                            onClick={_invoice}
                            isLoading={submitting}
                            disabled={submitting}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default BillingDetail;